import { useStorage, useEventListener } from '@vueuse/core';
import { computed, onMounted } from 'vue';
import { darkTheme } from 'naive-ui';
import type { GlobalTheme, GlobalThemeOverrides } from 'naive-ui';

export function useTheme() {
  const isDarkMode = useStorage('dark-mode', false);

  const currentTheme = computed((): GlobalTheme | null => {
    return isDarkMode.value ? darkTheme : null;
  });

  // Theme-aware CSS variables and colors
  const themeColors = computed(() => {
    if (isDarkMode.value) {
      return {
        // Dark mode colors
        primary: '#7cb2ca',
        primaryDark: '#122c58',
        background: '#1a1a1a',
        surface: '#2a2a2a',
        surfaceAlt: '#3a3a3a',
        border: '#444444',
        text: '#ffffff',
        textSecondary: '#cccccc',
        tableHeader: '#2a2a2a',
        tableRow: '#1a1a1a',
        tableRowHover: '#000000',
        frozenColumn: '#1e3a5f', // Dark blue for frozen columns
        highlight: '#2d3748', // Dark highlight
        coltConnectBg: '#2a3441', // Dark version of ColtConnect bg
        warning: '#fbbf24',
        success: '#10b981',
        error: '#ef4444'
      };
    } else {
      return {
        // Light mode colors
        primary: '#122c58',
        primaryDark: '#0f1f45',
        background: '#ffffff',
        surface: '#f8fafc',
        surfaceAlt: '#f1f5f9',
        border: '#e2e8f0',
        text: '#1a202c',
        textSecondary: '#4a5568',
        tableHeader: '#f7fafc',
        tableRow: '#ffffff',
        tableRowHover: '#fcd34d',
        frozenColumn: '#e6f7ff', // Light blue for frozen columns
        highlight: '#fff9c4', // Light highlight
        coltConnectBg: '#D8EEF4', // Original ColtConnect bg
        warning: '#f59e0b',
        success: '#059669',
        error: '#dc2626'
      };
    }
  });

  const themeOverrides = computed((): GlobalThemeOverrides => {
    const colors = themeColors.value;

    // PrimeVue-inspired border radius values
    const primeBorderRadius = {
      borderRadius: '6px',
      borderRadiusSmall: '4px',
      borderRadiusMedium: '6px',
      borderRadiusLarge: '8px'
    };

    // PrimeVue-inspired box shadow values
    const primeShadows = {
      boxShadowSmall: '0 2px 4px 0 rgba(0, 0, 0, 0.1)',
      boxShadowMedium: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
      boxShadowLarge: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
      boxShadowFocus: isDarkMode.value
        ? '0 0 0 3px rgba(124, 178, 202, 0.2)'
        : '0 0 0 3px rgba(18, 44, 88, 0.1)',
      boxShadowHover: '0 6px 12px -2px rgba(0, 0, 0, 0.15), 0 3px 7px -3px rgba(0, 0, 0, 0.1)'
    };

    const baseOverrides = {
      common: {
        primaryColor: colors.primary,
        primaryColorHover: colors.primary,
        successColor: colors.success,
        infoColor: colors.primary,
        warningColor: colors.warning,
        errorColor: colors.error,
        ...primeBorderRadius
      }
    };

    if (isDarkMode.value) {
      return {
        ...baseOverrides,
        common: {
          ...baseOverrides.common,
          bodyColor: colors.background,
          cardColor: colors.surface,
          modalColor: colors.surface,
          popoverColor: colors.surface,
          tableHeaderColor: colors.tableHeader,
          tableColor: colors.tableRow,
          railColor: colors.surfaceAlt,
          borderColor: colors.border,
          textColorBase: colors.text,
          textColor1: colors.text,
          textColor2: colors.textSecondary
        },
        Button: {
          borderRadius: primeBorderRadius.borderRadius,
          boxShadow: primeShadows.boxShadowSmall,
          boxShadowHover: primeShadows.boxShadowHover,
          boxShadowFocus: primeShadows.boxShadowFocus
        },
        Input: {
          borderRadius: primeBorderRadius.borderRadius,
          boxShadowFocus: primeShadows.boxShadowFocus
        },
        Select: {
          borderRadius: primeBorderRadius.borderRadius,
          boxShadowFocus: primeShadows.boxShadowFocus
        },
        Card: {
          borderRadius: primeBorderRadius.borderRadius,
          boxShadow: primeShadows.boxShadowMedium
        },
        Modal: {
          borderRadius: primeBorderRadius.borderRadiusLarge,
          boxShadow: primeShadows.boxShadowLarge
        },
        Dropdown: {
          borderRadius: primeBorderRadius.borderRadius,
          boxShadow: primeShadows.boxShadowMedium
        },
        Menu: {
          borderRadius: primeBorderRadius.borderRadius,
          boxShadow: primeShadows.boxShadowMedium
        },
        Form: {
          borderRadius: primeBorderRadius.borderRadius
        },
        DataTable: {
          thColor: colors.tableHeader,
          tdColor: colors.tableRow,
          tdColorHover: colors.tableRowHover,
          borderColor: colors.border,
          borderRadius: primeBorderRadius.borderRadiusSmall,
          boxShadow: primeShadows.boxShadowSmall
        },
        Tabs: {
          borderRadius: primeBorderRadius.borderRadius
        },
        Dialog: {
          borderRadius: primeBorderRadius.borderRadiusLarge,
          boxShadow: primeShadows.boxShadowLarge
        }
      };
    }

    return {
      ...baseOverrides,
      Button: {
        borderRadius: primeBorderRadius.borderRadius,
        boxShadow: primeShadows.boxShadowSmall,
        boxShadowHover: primeShadows.boxShadowHover,
        boxShadowFocus: primeShadows.boxShadowFocus
      },
      Input: {
        borderRadius: primeBorderRadius.borderRadius,
        boxShadowFocus: primeShadows.boxShadowFocus
      },
      Select: {
        borderRadius: primeBorderRadius.borderRadius,
        boxShadowFocus: primeShadows.boxShadowFocus
      },
      Card: {
        borderRadius: primeBorderRadius.borderRadius,
        boxShadow: primeShadows.boxShadowMedium
      },
      Modal: {
        borderRadius: primeBorderRadius.borderRadiusLarge,
        boxShadow: primeShadows.boxShadowLarge
      },
      Dropdown: {
        borderRadius: primeBorderRadius.borderRadius,
        boxShadow: primeShadows.boxShadowMedium
      },
      Menu: {
        borderRadius: primeBorderRadius.borderRadius,
        boxShadow: primeShadows.boxShadowMedium
      },
      Form: {
        borderRadius: primeBorderRadius.borderRadius
      },
      DataTable: {
        borderColor: colors.border,
        borderRadius: primeBorderRadius.borderRadiusSmall,
        boxShadow: primeShadows.boxShadowSmall
      },
      Tabs: {
        borderRadius: primeBorderRadius.borderRadius
      },
      Dialog: {
        borderRadius: primeBorderRadius.borderRadiusLarge,
        boxShadow: primeShadows.boxShadowLarge
      }
    };
  });

  const toggleTheme = () => {
    isDarkMode.value = !isDarkMode.value;
  };

  // Add keyboard shortcut (Ctrl+Shift+T or Cmd+Shift+T)
  onMounted(() => {
    useEventListener('keydown', (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'T') {
        e.preventDefault();
        toggleTheme();
      }
    });
  });

  return {
    isDarkMode,
    currentTheme,
    themeOverrides,
    themeColors,
    toggleTheme
  };
}
